import { useCallback, useEffect, useState } from 'react';
import { WebSocketManager } from '../utils/WebSocketManager';
/**
 * Hook for managing a real-time websocket connection for use with real-time voice mode.
 *
 * @param url - the base url of the endpoint to connect to
 * @param locale - the locale code of the user
 * @param realtimeContext - contextual information about the user
 * @param onMessage - callback to fire whenever a new messag is received
 * @param analytics - pcln logger instance
 * @returns object containing the state of the websocket connection and a function sendMessage which allows sending messages over the connection:
 * - `isConnected` - Whether or not the websocket is connected
 * - `sendMessage` - a function that can send a message over the connection
 */
export function useRealTimeWebSocket({
  url,
  locale = 'en-us',
  realtimeContext,
  onMessage,
  analytics,
  pennysid,
  requestFrom
}) {
  const [isConnected, setIsConnected] = useState(false);
  const wsManager = WebSocketManager.getInstance();
  const connectionKey = `${url}-${locale}`;
  const getWebSocketUrl = useCallback(baseUrl => {
    if (process.env.NODE_ENV === 'development') {
      switch (window.location.hostname) {
        case 'localhost':
          return 'ws://0.0.0.0:8080';
        case 'local.priceline.com':
          return 'wss://local.priceline.com';
        default:
          return baseUrl;
      }
    }
    return baseUrl.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
  }, []);
  useEffect(() => {
    const wsUrl = getWebSocketUrl(url);
    const fullUrl = `${wsUrl}/genai-svc/genai/realtime/chat?locale=${locale}&pennysid=${pennysid}&requestFrom=${requestFrom}`;
    const ws = wsManager.createConnection(connectionKey, fullUrl);
    const handleOpen = () => {
      setIsConnected(true);
      // Setup ping interval
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);
      return pingInterval;
    };
    const handleClose = event => {
      setIsConnected(false);
      if (event.code !== 1005) {
        analytics.logError({
          loggedFrom: 'useRealTimeWebSocket.onclose',
          message: `WebSocket closed - Code: ${event.code}, Reason: ${event.reason}`,
          error: event
        });
      }
    };
    const handleError = error => {
      analytics.logError({
        loggedFrom: 'useRealTimeWebSocket.onerror',
        message: 'WebSocket encountered an error',
        error
      });
      setIsConnected(false);
    };
    let pingInterval = null;
    // Only set up listeners if we don't already have them
    if (ws.readyState === WebSocket.CONNECTING) {
      ws.onopen = () => {
        pingInterval = handleOpen();
      };
      ws.onclose = handleClose;
      ws.onerror = handleError;
    } else if (ws.readyState === WebSocket.OPEN) {
      pingInterval = handleOpen();
    }
    wsManager.addMessageListener(connectionKey, onMessage);
    return () => {
      wsManager.removeMessageListener(connectionKey, onMessage);
      if (pingInterval) {
        clearInterval(pingInterval);
      }
      wsManager.closeConnection(connectionKey);
    };
  }, [url, locale, realtimeContext, onMessage, analytics, getWebSocketUrl, connectionKey]);
  const sendMessage = useCallback(message => {
    const ws = wsManager.getConnection(connectionKey);
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket is not connected');
      return;
    }
    const messageStr = typeof message === 'string' ? message : JSON.stringify(message);
    ws.send(messageStr);
  }, [connectionKey]);
  return {
    isConnected,
    sendMessage
  };
}